<template>
  <div class="flex flex-row-reverse items-center h-8">
    <NumberFormatter
      v-if="selectedPoint"
      #="{ number }"
      :value="selectedPoint.value"
    >
      <NText
        dir="ltr"
        weight="bold"
        variant="h3"
        class="!text-[12px] mr-1 mt-[2px]"
      >
        {{ number }}
      </NText>
    </NumberFormatter>

    <template v-else>
      <span>
        <NumberFormatter #="{ number }" :value="symbol.lastTradePrice ?? 0">
          <ChangePriceEffect :price="symbol.lastTradePrice ?? 0">
            <NText
              :key="number"
              variant="h3"
              weight="bold"
              class="mr-1 cursor-default !text-[12px]"
            >
              {{ number }}
            </NText>
          </ChangePriceEffect>
        </NumberFormatter>
      </span>
    </template>

    <div class="flex justify-between mt-1">
      <NText
        v-if="!selectedPoint && firstPointOfChart.value"
        class="text-neutral-secondary"
        weight="regular"
        variant="overline"
      >
        {{ filterLabel }}
      </NText>

      <NumberFormatter
        v-if="!isNaN(percentage)"
        #="{ number }"
        :value="percentage"
        sign-display="exceptZero"
        maximum-fraction-digits="2"
        number-style="percent"
      >
        <NText
          dir="ltr"
          class="mr-1 !text-[12px]"
          :class="{
            'text-neutral-secondary': !percentage,
            'text-critical-primary': percentage < 0,
            'text-brand-primary': percentage > 0,
          }"
          data-testid="percentage-number"
          weight="bold"
          variant="caption1"
        >
          ({{ number }})
        </NText>
      </NumberFormatter>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { LineData } from 'lightweight-charts';
import { SECONDS_IN_DAY } from '@core/symbol';
import { isEmpty } from '@libs/utils';

import NumberFormatter from '@/components/Number.vue';
import ChangePriceEffect from '@/components/ChangePriceEffect.vue';

import {
  SYMBOL_LINE_CHART_FILTERS,
  useActiveSymbolIsin,
  useLiveSymbolQuery,
} from '@/modules/symbol';

type ChartInfoPropsType = {
  selectedPoint?: LineData;
  firstPointOfChart: any;
  lastPointOfChart: any;
  selectedTimespan: string;
};
type ChartInfoEmitsType = {
  (e: 'percentageChanged', percentage: number): void;
};

const props = defineProps<ChartInfoPropsType>();
const emit = defineEmits<ChartInfoEmitsType>();

const { t } = useI18n();

const { activeIsin } = useActiveSymbolIsin();
const { symbol } = useLiveSymbolQuery(activeIsin);

const showTodayData = computed(
  () => props.selectedTimespan === SYMBOL_LINE_CHART_FILTERS.oneDay.id
);

const percentage = computed(() => {
  if (props.selectedPoint) {
    return (
      props.selectedPoint.value /
        (showTodayData.value
          ? symbol.value.referencePrice
          : props.firstPointOfChart.value) -
      1
    );
  }

  if (
    showTodayData.value &&
    !isEmpty(symbol.value.referencePriceVariationPercentage)
  ) {
    return (symbol.value.referencePriceVariationPercentage ?? 0) / 100;
  } else if (symbol.value.lastTradePrice && props.firstPointOfChart.value) {
    return symbol.value.lastTradePrice / props.firstPointOfChart.value - 1;
  }

  return 0;
});

const filterLabel = computed(() => {
  if (showTodayData.value) {
    return t('misc.today');
  }

  const diffSeconds =
    props.lastPointOfChart.time - props.firstPointOfChart.time;

  return t('symbol.charts.differenceDays', {
    days: Math.floor(diffSeconds / SECONDS_IN_DAY) + 1,
  });
});

watch(
  () => percentage.value,
  () => {
    emit('percentageChanged', percentage.value);
  },
  { immediate: true }
);
</script>
